/**
 * HousingCommitteeDecision — the grant/loan award form, shared by the case file
 * and the committee session workspace.
 *
 * The original lived inline in a 1168-line case file with `useState('800000')` as
 * the amount, so every case opened pre-filled with the same figure regardless of
 * the applicant's income. It now starts from the suggestion the platform computes
 * from the declared income, states the rule that produced it, and spells out what
 * each assistance type obliges the citizen to do next — because choosing "loan"
 * silently starts a 90-day clock the committee should know about.
 */
import { useEffect, useState } from 'react';
import type { HousingCommitteeSuggestion, HousingPriorityView } from '@servicesai/api-client';
import {
  FactorBars,
  GlassButton,
  GlassInput,
  ProvenanceChip,
  formatAed,
  useI18n,
  type Localized,
} from '@servicesai/ui';

const COPY = {
  title: { en: 'Committee decision', ar: 'قرار اللجنة' },
  reasonEn: { en: 'Reason (English)', ar: 'السبب (إنجليزي)' },
  reasonAr: { en: 'Reason (Arabic)', ar: 'السبب (عربي)' },
  reasonHint: {
    en: 'Both languages are required. The citizen reads this on their Qarar.',
    ar: 'اللغتان مطلوبتان. يقرأ المواطن هذا في قراره.',
  },
  amount: { en: 'Amount (AED)', ar: 'المبلغ (درهم)' },
  suggested: { en: 'Suggested', ar: 'المقترح' },
  useSuggested: { en: 'Use suggested amount', ar: 'استخدم المبلغ المقترح' },
  approve: { en: 'Approve', ar: 'موافقة' },
  reject: { en: 'Reject', ar: 'رفض' },
  grant: { en: 'Grant', ar: 'منحة' },
  loan: { en: 'Loan', ar: 'قرض' },
  grantConsequence: {
    en: 'No bank file. The citizen goes straight to the financing contract with a consultant.',
    ar: 'لا ملف مصرفي. ينتقل المواطن مباشرة إلى عقد التمويل مع استشاري.',
  },
  loanConsequence: {
    en: 'The citizen must open an implementation file with one of the programme banks within 90 days of the Qarar.',
    ar: 'على المواطن فتح ملف تنفيذ لدى أحد مصارف البرنامج خلال ٩٠ يوماً من القرار.',
  },
  preview: { en: 'What the Qarar will say', ar: 'ما سيذكره القرار' },
  previewAmount: { en: 'Approved amount', ar: 'المبلغ المعتمد' },
  previewType: { en: 'Assistance type', ar: 'نوع المساعدة' },
  amountInvalid: { en: 'Enter an amount above zero.', ar: 'أدخل مبلغاً أكبر من صفر.' },
  priority: { en: 'Household priority', ar: 'أولوية الأسرة' },
  priorityCaption: { en: 'Queue order, not the award', ar: 'ترتيب القائمة وليس المبلغ' },
};

export interface CommitteeDecisionInput {
  outcome: 'approved' | 'rejected';
  assistanceType?: 'grant' | 'loan';
  amountAed?: number;
  reasons: Localized[];
}

export function HousingCommitteeDecision({
  suggestion,
  priority,
  busy,
  onDecide,
}: {
  suggestion: HousingCommitteeSuggestion | null;
  priority: HousingPriorityView | null;
  busy?: boolean;
  onDecide: (input: CommitteeDecisionInput) => void;
}) {
  const { l, lang } = useI18n();
  const [assistanceType, setAssistanceType] = useState<'grant' | 'loan'>(
    suggestion?.assistanceType ?? 'grant',
  );
  const [amount, setAmount] = useState(String(suggestion?.amountAed ?? ''));
  const [reasonEn, setReasonEn] = useState('');
  const [reasonAr, setReasonAr] = useState('');

  // A new case arrives with its own suggestion; the session workspace reuses this
  // component across cases, so the form must follow rather than keep the last one.
  useEffect(() => {
    setAssistanceType(suggestion?.assistanceType ?? 'grant');
    setAmount(String(suggestion?.amountAed ?? ''));
    setReasonEn('');
    setReasonAr('');
  }, [suggestion]);

  const amountValue = Math.round(Number(amount.replace(/,/g, '')) || 0);
  const reasonsGiven = Boolean(reasonEn.trim() && reasonAr.trim());
  const canApprove = reasonsGiven && amountValue > 0;

  const reasons = (): Localized[] => [{ en: reasonEn.trim(), ar: reasonAr.trim() }];

  return (
    <section className="space-y-4">
      {priority ? (
        <div className="rounded-xl border border-aeblack-100 bg-white p-4">
          <h4 className="text-xs font-extrabold text-ink">{l(COPY.priority)}</h4>
          <FactorBars
            className="mt-2.5"
            factors={priority.factors}
            band={priority.band}
            score={priority.score}
            summary={priority.summary}
            caption={COPY.priorityCaption}
          />
        </div>
      ) : null}

      <div>
        <h4 className="text-xs font-extrabold text-ink">{l(COPY.previewType)}</h4>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {(['grant', 'loan'] as const).map((type) => {
            const active = assistanceType === type;
            return (
              <button
                key={type}
                type="button"
                aria-pressed={active}
                onClick={() => setAssistanceType(type)}
                className={`rounded-xl border p-3.5 text-start ${
                  active
                    ? 'border-aegold-600 bg-aegold-50 ring-1 ring-aegold-600/25'
                    : 'border-aeblack-200 bg-white hover:border-aeblack-300'
                }`}
              >
                <span className="text-xs font-black text-ink">
                  {l(type === 'grant' ? COPY.grant : COPY.loan)}
                </span>
                <span className="mt-1 block text-[11px] leading-5 text-ink-soft">
                  {l(type === 'grant' ? COPY.grantConsequence : COPY.loanConsequence)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-soft">
          {l(COPY.amount)}
          <GlassInput
            className="mt-1 max-w-xs"
            value={amount}
            onChange={(event) => setAmount(event.target.value.replace(/[^\d,]/g, ''))}
            dir="ltr"
            aria-invalid={amount !== '' && amountValue <= 0}
          />
        </label>
        {suggestion ? (
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <span className="text-[11px] font-bold text-ink-soft">
              {l(COPY.suggested)}:{' '}
              <span className="font-mono tabular-nums" dir="ltr">
                {suggestion.amountAed.toLocaleString('en-AE')}
              </span>
            </span>
            {amountValue !== suggestion.amountAed ? (
              <button
                type="button"
                onClick={() => {
                  setAmount(String(suggestion.amountAed));
                  setAssistanceType(suggestion.assistanceType);
                }}
                className="text-[11px] font-black text-seablue-800 underline underline-offset-2"
              >
                {l(COPY.useSuggested)}
              </button>
            ) : null}
            <ProvenanceChip kind="deterministic" />
          </div>
        ) : null}
        {suggestion ? (
          <p className="mt-1.5 text-[11px] leading-5 text-ink-faint">{l(suggestion.basis)}</p>
        ) : null}
        {amount !== '' && amountValue <= 0 ? (
          <p className="mt-1.5 text-[11px] font-bold text-reject">{l(COPY.amountInvalid)}</p>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-semibold text-ink-soft">
          {l(COPY.reasonEn)}
          <textarea
            className="mt-1 min-h-20 w-full resize-y rounded-lg border border-aeblack-200 bg-white px-3 py-2 text-xs leading-6 text-ink"
            value={reasonEn}
            onChange={(event) => setReasonEn(event.target.value)}
            dir="ltr"
          />
        </label>
        <label className="block text-xs font-semibold text-ink-soft">
          {l(COPY.reasonAr)}
          <textarea
            className="mt-1 min-h-20 w-full resize-y rounded-lg border border-aeblack-200 bg-white px-3 py-2 text-xs leading-6 text-ink"
            value={reasonAr}
            onChange={(event) => setReasonAr(event.target.value)}
            dir="rtl"
          />
        </label>
      </div>
      <p className="text-[11px] leading-5 text-ink-faint">{l(COPY.reasonHint)}</p>

      {canApprove ? (
        <div className="rounded-xl border border-aegold-500/35 bg-aegold-50 p-3.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-gold-deep">
            {l(COPY.preview)}
          </div>
          <dl className="mt-2 grid gap-x-5 gap-y-1.5 sm:grid-cols-2">
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-[11px] font-bold text-ink-soft">{l(COPY.previewType)}</dt>
              <dd className="text-xs font-black text-ink">
                {l(assistanceType === 'grant' ? COPY.grant : COPY.loan)}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-[11px] font-bold text-ink-soft">{l(COPY.previewAmount)}</dt>
              <dd className="font-mono text-xs font-black tabular-nums text-ink" dir="ltr">
                {formatAed(amountValue, lang)}
              </dd>
            </div>
          </dl>
          <p className="mt-2 border-t border-aegold-500/25 pt-2 text-[11px] leading-5 text-ink-soft">
            {l(assistanceType === 'grant' ? COPY.grantConsequence : COPY.loanConsequence)}
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <GlassButton
          variant="primary"
          disabled={busy || !canApprove}
          onClick={() =>
            onDecide({
              outcome: 'approved',
              assistanceType,
              amountAed: amountValue,
              reasons: reasons(),
            })
          }
        >
          {l(COPY.approve)}
        </GlassButton>
        <GlassButton
          variant="danger"
          disabled={busy || !reasonsGiven}
          onClick={() => onDecide({ outcome: 'rejected', reasons: reasons() })}
        >
          {l(COPY.reject)}
        </GlassButton>
      </div>
    </section>
  );
}
